import { useEffect, useState, useRef, useCallback } from "react";
import { api, ASSET_TYPE_LABELS, LIABILITY_TYPE_LABELS } from "@/lib/api";
import { MAX_UPLOAD_BYTES, runIngestBatch, validateFiles } from "@/lib/ingest-upload";
import { useDemo } from "@/context/DemoContext";
import { useHousehold } from "@/context/HouseholdContext";
import KnownSendersManager from "@/components/comms/KnownSendersManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tray, FilePdf, Trash, CheckCircle, Sparkle, ArrowRight, Plus, X as XIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import UpsellCard from "@/components/UpsellCard";
import ConfirmDuplicateDialog from "@/components/ConfirmDuplicateDialog";
import DuplicateNotice from "@/components/DuplicateNotice";
import IngestCreateDialog from "@/components/IngestCreateDialog";
import DocumentReviewTable, { buildInitialFacts } from "@/components/DocumentReviewTable";
import NamedInputDialog from "@/components/NamedInputDialog";

const BASE_DOC_TYPES = {
  lease: "Lease",
  insurance_declaration: "Insurance Declaration",
  property_tax: "Property Tax",
  pm_statement: "PM Statement",
  mortgage_statement: "Mortgage Statement",
  mineral_statement: "Mineral / Royalty Statement",
  other: "Other Document",
};

// What the right-hand picker should file each document type to.
const TARGET_KIND = {
  lease: "real_estate", insurance_declaration: "real_estate", property_tax: "real_estate",
  pm_statement: "real_estate", mineral_statement: "mineral_royalty",
  mortgage_statement: "liability", other: "any",
};

const slugify = (s) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);
const getErrorDetail = (e) => e?.response?.data?.detail;
const getErrorMessage = (e, fallback) => {
  const detail = getErrorDetail(e);
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") return detail.message || detail.error || fallback;
  return fallback;
};
const uploadLimitLabel = `${Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))} MB`;

const fmt$ = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function MineralPanel({ summary }) {
  const t = summary.totals || {};
  const h = summary.header || {};
  return (
    <div
      data-testid="ingest-mineral-panel"
      className="mt-4 rounded-xl border border-[hsl(var(--ploy-border))] bg-[hsl(var(--ploy-panel))] p-5"
    >
      <div className="flex items-start justify-between mb-4 gap-4">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[hsl(var(--ploy-brass-deep))]">
            Mineral statement parsed
          </p>
          <p className="font-serif text-2xl leading-tight mt-1 text-[hsl(var(--ploy-ink))] truncate">
            {h.operator || "Operator"}
          </p>
          <p className="text-[12px] text-[hsl(var(--ploy-ink-soft))] mt-1">
            Owner code <span className="font-mono text-[hsl(var(--ploy-ink))]">{h.owner_code || "—"}</span>
            {" · "}Check #{h.check_number || "—"} on {h.check_date || "—"}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[hsl(var(--ploy-brass-deep))]">
            Paid Total
          </p>
          <p className="font-serif text-3xl font-medium tabular-nums mt-1 text-[hsl(var(--ploy-ink))]">
            {fmt$(h.paid_total || t.paid_total)}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-[hsl(var(--ploy-border))]">
        <Stat label="Wells"             value={(summary.well_count || 0).toLocaleString()} />
        <Stat label="Production lines"  value={(summary.line_count || 0).toLocaleString()} />
        <Stat label="Period"            value={`${summary.period_min || "—"} → ${summary.period_max || "—"}`} />
        <Stat label="Pages"             value={(summary.pages || 0).toLocaleString()} />
      </div>
      <div className="mt-4 pt-4 border-t border-[hsl(var(--ploy-border))] flex items-center gap-2 text-[12px]">
        {summary.cross_foot_ok ? (
          <>
            <CheckCircle size={14} weight="fill" className="text-[hsl(var(--ploy-pos))]" />
            <span className="text-[hsl(var(--ploy-pos))] font-semibold">Cross-foot OK</span>
            <span className="text-[hsl(var(--ploy-ink-soft))]">— production lines sum to the cover total exactly.</span>
          </>
        ) : (
          <>
            <span className="text-[hsl(var(--ploy-rust))] font-semibold">
              Cross-foot delta {summary.cross_foot_delta >= 0 ? "+" : ""}{fmt$(summary.cross_foot_delta)}
            </span>
            <span className="text-[hsl(var(--ploy-ink-soft))]">— review before filing.</span>
          </>
        )}
      </div>
    </div>
  );
}
const Stat = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--ploy-brass-deep))] font-semibold">
      {label}
    </p>
    <p className="font-serif text-xl font-medium tabular-nums mt-1 text-[hsl(var(--ploy-ink))] truncate">
      {value}
    </p>
  </div>
);

function IngestCard({ item, assets, liabilities, hid, onDone, customTypes, addCustomType, addPackage, refreshAssets, refreshLiabilities, viewDuplicate }) {
  const [dt, setDt] = useState(item.doc_type || "other");
  const [aid, setAid] = useState(item.suggested_asset_id || "none");
  const [lid, setLid] = useState(item.suggested_liability_id || "none");
  const [unitId, setUnitId] = useState("none");
  const [units, setUnits] = useState([]);
  const p = item.parsed || {};
  const [busy, setBusy] = useState(false);
  const [upsell, setUpsell] = useState(null);
  const [pendingDuplicate, setPendingDuplicate] = useState(null);
  const [createDialog, setCreateDialog] = useState({ open: false, kind: null });
  const [customTypeDialogOpen, setCustomTypeDialogOpen] = useState(false);

  // Structured facts for the review table (editable)
  const [facts, setFacts] = useState(() => buildInitialFacts(p, item.doc_type || "other"));

  // Re-derive facts when the underlying parsed data changes (e.g. after Re-parse · Opus)
  useEffect(() => {
    setFacts(buildInitialFacts(item.parsed || {}, item.doc_type || "other"));
  }, [item.id, item.parsed, item.doc_type]); // reset on item change or re-parse

  const ALL_TYPES = { ...BASE_DOC_TYPES, ...customTypes };
  const kind = TARGET_KIND[dt] || "any";
  const assetChoices =
    kind === "real_estate" ? assets.filter((a) => a.asset_type === "real_estate")
    : kind === "mineral_royalty" ? assets.filter((a) => a.asset_type === "mineral_royalty")
    : assets;

  useEffect(() => {
    if (dt === "lease" && aid && aid !== "none") {
      api.get(`/assets/${aid}/operating?household_id=${hid}`).then((r) => setUnits(r.data.units || [])).catch(() => setUnits([]));
    }
  }, [dt, aid, hid]);

  const onTypeChange = (v) => {
    if (v === "__add__") {
      setCustomTypeDialogOpen(true);
      return;
    }
    setDt(v);
  };

  const view = async () => {
    try {
      const r = await api.get(`/ingest/${item.id}/file?household_id=${hid}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("File unavailable"); }
  };

  const confirm = async () => {
    if (kind === "liability") {
      if (!lid || lid === "none") { toast.error("Choose the mortgage this belongs to"); return; }
    } else if (kind === "real_estate" || kind === "mineral_royalty") {
      if (!aid || aid === "none") { toast.error("Choose a target"); return; }
    }
    if (dt === "lease" && (!unitId || unitId === "none")) { toast.error("Choose the unit"); return; }

    // Build payload from the reviewed/edited facts.
    // Map fact values to confirm payload fields by matching key OR field label.
    const FIELD_MAP = {
      // Direct key matches
      current_balance: "current_balance", monthly_payment: "monthly_payment", due_date: "due_date",
      tenant_name: "tenant_name", start_date: "start_date", end_date: "end_date",
      monthly_rent: "monthly_rent", security_deposit: "security_deposit",
      tax_year: "tax_year", tax_amount: "tax_amount", tax_status: "tax_status",
      loan_number: "loan_number",
    };
    // Field label → payload key (for AI-extracted facts whose key is 'ai_fact_*')
    const LABEL_MAP = {
      "outstanding principal balance": "current_balance", "principal balance": "current_balance",
      "current balance": "current_balance", "new balance": "current_balance", "unpaid balance": "current_balance",
      "monthly payment": "monthly_payment", "payment amount": "monthly_payment", "amount due": "monthly_payment",
      "total amount due": "monthly_payment",
      "payment due date": "due_date", "due date": "due_date", "next due": "due_date", "next due date": "due_date",
      "tenant name": "tenant_name", "tenant": "tenant_name",
      "lease start": "start_date", "start date": "start_date", "coverage start": "start_date",
      "lease end": "end_date", "end date": "end_date", "coverage end": "end_date",
      "monthly rent": "monthly_rent", "rent": "monthly_rent",
      "security deposit": "security_deposit", "deposit": "security_deposit",
      "tax year": "tax_year", "year": "tax_year",
      "tax amount": "tax_amount", "amount": "tax_amount",
      "loan number": "loan_number", "account number": "loan_number",
    };

    const resolved = {};
    for (const f of facts) {
      const direct = FIELD_MAP[f.key];
      if (direct) { resolved[direct] = f.value; continue; }
      const byLabel = LABEL_MAP[(f.field || "").toLowerCase().trim()];
      if (byLabel && !(byLabel in resolved)) { resolved[byLabel] = f.value; }
    }

    const num = (v) => (v === "" || v == null) ? null : Number(v);
    const str = (v) => (v == null ? "" : String(v));
    const payload = {
      doc_type: dt,
      target_asset_id: kind === "liability" || aid === "none" ? null : aid,
      target_unit_id: unitId === "none" ? null : unitId,
      target_liability_id: kind === "liability" && lid !== "none" ? lid : null,
      label: item.file?.filename || "",
      tenant_name: str(resolved.tenant_name ?? p.tenant_name),
      start_date: str(resolved.start_date ?? p.start_date) || null,
      end_date: str(resolved.end_date ?? p.end_date) || null,
      monthly_rent: num(resolved.monthly_rent ?? p.monthly_rent),
      security_deposit: num(resolved.security_deposit ?? p.security_deposit),
      terms_summary: p.summary || "",
      tax_year: num(resolved.tax_year ?? p.year) || null,
      tax_amount: num(resolved.tax_amount ?? p.amount),
      tax_status: str(resolved.tax_status) || "paid",
      current_balance: num(resolved.current_balance ?? p.current_balance),
      monthly_payment: num(resolved.monthly_payment ?? p.monthly_payment),
      due_date: str(resolved.due_date ?? p.loan_due_date) || null,
      // APU cutover: send reviewed facts + operator identity
      extracted_facts: facts.map((f) => ({
        field: f.field, value: f.value,
        source_text: f.sourceText || "", confidence: f.confidence || "high",
        fact_type: null,
      })),
      decided_by: null,
    };
    const handleConfirmSuccess = (r) => {
      const msg = r.data.destination || "Filed";
      if (r.data.processing) {
        toast.success(msg, {
          description: "Indexing wells and production lines in the background. They'll appear in Oil & Gas within a minute.",
          duration: 8000,
        });
      } else {
        toast.success(msg);
      }
      onDone();
    };
    setBusy(true);
    try {
      const r = await api.post(`/ingest/${item.id}/confirm?household_id=${hid}`, payload);
      handleConfirmSuccess(r);
    } catch (e) {
      // 402 Payment Required → show Upsell Card with original payload so user
      // can subscribe-and-retry, store-only, or park.
      const det = getErrorDetail(e);
      if (e?.response?.status === 402 && det?.error === "subscription_required") {
        setUpsell({ detail: det, payload });
      } else if (e?.response?.status === 409 && det?.error === "duplicate_document") {
        setPendingDuplicate({ detail: det, payload, onSuccess: handleConfirmSuccess });
      } else {
        toast.error(getErrorMessage(e, "Could not file"));
      }
    } finally { setBusy(false); }
  };

  const confirmDuplicateAnyway = async () => {
    if (!pendingDuplicate) return;
    setBusy(true);
    try {
      const r = await api.post(`/ingest/${item.id}/confirm?household_id=${hid}`, {
        ...pendingDuplicate.payload,
        confirm_duplicate: true,
      });
      setPendingDuplicate(null);
      pendingDuplicate.onSuccess?.(r);
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not file"));
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (!window.confirm("Discard this upload? The file will be deleted and can't be recovered.")) return;
    await api.delete(`/ingest/${item.id}?household_id=${hid}`);
    onDone();
  };

  const reparse = async (tier = "opus") => {
    setBusy(true);
    try {
      const r = await api.post(`/ingest/${item.id}/reparse?household_id=${hid}&tier=${tier}`);
      toast.success(`Re-parsed with ${r.data?.parse_trace?.tier_used || tier}`);
      onDone();
    } catch (e) { toast.error(getErrorMessage(e, "Re-parse failed")); }
    finally { setBusy(false); }
  };

  return (
    <div
      data-testid={`ingest-item-${item.id}`}
      className="bg-[hsl(var(--ploy-card))] border border-[hsl(var(--ploy-border))] rounded-2xl p-6 shadow-[0_1px_2px_hsl(var(--ploy-ink)/0.04)]"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={view}
          data-testid={`ingest-view-${item.id}`}
          className="inline-flex items-center gap-2 font-semibold text-[15px] text-[hsl(var(--ploy-rust))] hover:underline min-w-0"
        >
          <FilePdf size={18} weight="fill" /> <span className="truncate">{item.file?.filename || "Document"}</span>
        </button>
        <div className="flex items-center gap-2">
          {item.ai_used && (
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] rounded-full px-2.5 py-1 bg-[hsl(var(--ploy-rust-soft))] text-[hsl(var(--ploy-rust))] inline-flex items-center gap-1">
              <Sparkle size={11} weight="fill" /> AI
            </span>
          )}
          {item.parse_trace?.tier_used && (
            <span data-testid={`ingest-tier-${item.id}`} title={`class: ${item.parse_trace.doc_class || "?"} · ${item.parse_trace.chars_per_page || 0} chars/page · ${item.parse_trace.ms || 0}ms${item.parse_trace.gates_failed?.length ? ` · gates failed: ${item.parse_trace.gates_failed.join(", ")}` : ""}`}
                  className="text-[10px] font-bold uppercase tracking-[0.12em] rounded-full px-2.5 py-1 bg-[hsl(var(--ploy-bg-sec))] text-[hsl(var(--ploy-ink-soft))]">
              {item.parse_trace.tier_used}
            </span>
          )}
          {item.parse_trace?.gates_failed?.length > 0 && (
            <button data-testid={`ingest-reparse-${item.id}`} onClick={() => reparse("opus")} disabled={busy} className="text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-0.5 bg-warning/15 text-warning hover:bg-warning/25 disabled:opacity-50">
              Re-parse · Opus
            </button>
          )}
          {item.memory_used && <span data-testid={`ingest-memory-${item.id}`} className="text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-0.5 bg-success/15 text-success inline-flex items-center gap-1"><CheckCircle size={11} weight="fill" /> Remembered</span>}
          {item.routing && (
            <span
              data-testid={`ingest-routing-hint-${item.id}`}
              className="text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-0.5 bg-[hsl(var(--ploy-panel))] text-[hsl(var(--ploy-brass-deep))]"
            >
              {item.routing.auto_file
                ? "Auto-files"
                : `${item.routing.confirm_count || 0} of 3 confirms — then auto-files`}
            </span>
          )}
        </div>
      </div>
      {/* Original narrative hidden — available via "Show original summary" in the review table */}

      {/* DUPLICATE warning — P1 universal duplicate detection */}
      {item.duplicate_of && (
        <div data-testid={`ingest-duplicate-${item.id}`} className="mt-3">
          <DuplicateNotice duplicateOf={item.duplicate_of} onView={viewDuplicate} />
        </div>
      )}

      {/* UNRECOGNISED PACKAGE — mineral statement w/ no matching asset */}
      {item.requires_new_asset && dt === "mineral_statement" && !item.duplicate_of && (
        <div data-testid={`ingest-unrecognized-${item.id}`} className="mt-3 rounded-lg border border-[hsl(35_70%_75%)] bg-[hsl(35_70%_94%)] px-4 py-3">
          <div className="flex items-start gap-2.5">
            <span className="text-[10px] font-bold tracking-wider uppercase rounded-sm px-2 py-0.5 bg-[hsl(35_70%_46%)] text-white shrink-0 mt-0.5">Unrecognized</span>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-[hsl(28_55%_28%)]">
                Mineral statement detected, but no matching package on file.
              </p>
              <p className="text-[12px] text-[hsl(28_45%_36%)] mt-0.5">
                Appears to be: <span className="font-semibold">{item.suggested_package_name || "Unknown"}</span>
                {item.suggested_owner_code ? <> · owner code <span className="font-mono">{item.suggested_owner_code}</span></> : null}
              </p>
              <p className="text-[11px] text-[hsl(28_45%_44%)] mt-1">
                Use the &ldquo;+ Add package&hellip;&rdquo; option in the asset picker below to create it, then click &ldquo;File document&rdquo;.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={addPackage}
                className="mt-3 rounded-sm font-semibold"
              >
                + Add package
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Standardized Review Table (replaces narrative + type-specific forms) ── */}
      <DocumentReviewTable
        parsed={p}
        docType={dt}
        item={item}
        facts={facts}
        onFactsChange={setFacts}
      />

      {/* Mineral statement deep-parse summary (supplementary to review table) */}
      {dt === "mineral_statement" && p._mineral_summary && (
        <MineralPanel summary={p._mineral_summary} />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold">Document type</Label>
          <Select value={dt} onValueChange={onTypeChange}>
            <SelectTrigger data-testid={`ingest-type-${item.id}`} className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(ALL_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              <SelectItem value="__add__" data-testid={`ingest-add-type-${item.id}`} className="text-primary font-semibold">+ Add category…</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[11px] uppercase tracking-wider font-bold">
            {kind === "liability" ? "File to mortgage" : kind === "any" ? "File to (optional)" : "File to asset"}
          </Label>
          {kind === "liability" ? (
            <Select value={lid} onValueChange={(v) => {
              if (v === "__add_liability__") { setCreateDialog({ open: true, kind: "liability" }); return; }
              setLid(v);
            }}>
              <SelectTrigger data-testid={`ingest-liability-${item.id}`} className="rounded-sm mt-1"><SelectValue placeholder="Choose mortgage" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {liabilities.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} · {LIABILITY_TYPE_LABELS[l.liability_type] || l.liability_type}</SelectItem>)}
                <SelectItem value="__add_liability__" data-testid={`ingest-add-liability-${item.id}`} className="text-primary font-semibold">+ Add new mortgage…</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Select
              value={aid}
              onValueChange={(v) => {
                if (v === "__add_asset__") {
                  const assetKind = kind === "mineral_royalty" ? "mineral_royalty" : "real_estate";
                  setCreateDialog({ open: true, kind: assetKind });
                  return;
                }
                setAid(v);
              }}
            >
              <SelectTrigger data-testid={`ingest-asset-${item.id}`} className="rounded-sm mt-1"><SelectValue placeholder="Choose asset" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {assetChoices.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {ASSET_TYPE_LABELS[a.asset_type] || a.asset_type}</SelectItem>)}
                <SelectItem value="__add_asset__" data-testid={`ingest-add-asset-${item.id}`} className="text-primary font-semibold">
                  + Add new {kind === "mineral_royalty" ? "package" : kind === "real_estate" ? "property" : "asset"}…
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {kind === "liability" && liabilities.length === 0 && (
        <p className="text-xs text-muted-foreground mt-2">No mortgages on file yet — use the &ldquo;+ Add new mortgage&rdquo; option above to create one.</p>
      )}

      {/* Unit picker for leases — still needed as a filing control */}
      {dt === "lease" && (
        <div className="mt-3">
          <Label className="text-[11px] uppercase tracking-wider font-bold">Unit</Label>
          <Select value={unitId} onValueChange={(v) => {
            if (v === "__add_unit__") { setCreateDialog({ open: true, kind: "unit" }); return; }
            setUnitId(v);
          }}>
            <SelectTrigger data-testid={`ingest-unit-${item.id}`} className="rounded-sm mt-1 max-w-xs"><SelectValue placeholder="Unit" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Unit —</SelectItem>
              {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.label}</SelectItem>)}
              {aid && aid !== "none" && (
                <SelectItem value="__add_unit__" data-testid={`ingest-add-unit-${item.id}`} className="text-primary font-semibold">+ Add new unit…</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {upsell && (
        <UpsellCard
          detail={upsell.detail}
          householdId={hid}
          ingestItem={item}
          confirmPayload={upsell.payload}
          onSubscribed={() => { setUpsell(null); onDone(); }}
          onStored={() => { setUpsell(null); onDone(); }}
          onPark={() => { setUpsell(null); toast.success("Parked — we'll surface it again later."); }}
          onClose={() => setUpsell(null)}
        />
      )}

      <ConfirmDuplicateDialog
        open={!!pendingDuplicate}
        onOpenChange={(open) => {
          if (!open && !busy) setPendingDuplicate(null);
        }}
        detail={pendingDuplicate?.detail}
        busy={busy}
        onConfirm={confirmDuplicateAnyway}
        onView={viewDuplicate}
      />

      {/* Universal "Add new" creation dialog */}
      <IngestCreateDialog
        open={createDialog.open}
        kind={createDialog.kind}
        parsed={p}
        hid={hid}
        assets={assets}
        parentAssetId={aid !== "none" ? aid : undefined}
        onClose={() => setCreateDialog({ open: false, kind: null })}
        onCreate={(newId) => {
          // Auto-select the newly created item in the appropriate dropdown
          if (createDialog.kind === "liability") {
            refreshLiabilities?.();
            setLid(newId);
          } else if (createDialog.kind === "unit") {
            // Refresh units list for the selected asset
            if (aid && aid !== "none") {
              api.get(`/assets/${aid}/operating?household_id=${hid}`).then((r) => setUnits(r.data.units || [])).catch(() => {});
            }
            setUnitId(newId);
          } else {
            refreshAssets?.();
            setAid(newId);
          }
        }}
      />

      <NamedInputDialog
        open={customTypeDialogOpen}
        onOpenChange={setCustomTypeDialogOpen}
        title="Add document category"
        description="Create a category label for this document type."
        label="Category name"
        placeholder="For example, HOA statement"
        initialValue=""
        confirmLabel="Add category"
        busyLabel="Adding…"
        testId={`ingest-custom-type-dialog-${item.id}`}
        validate={(value) => {
          if (!value) return "Enter a category name.";
          if (!slugify(value)) return "Use at least one letter or number.";
          return "";
        }}
        onSubmit={async (name) => {
          const slug = slugify(name);
          try {
            const created = await addCustomType(slug, name);
            if (created) setDt(created);
          } catch (e) {
            toast.error(getErrorMessage(e, "Could not add category"));
            throw e;
          }
        }}
      />

      <div className="flex items-center justify-between mt-5 pt-4 border-t border-[hsl(var(--ploy-border))]">
        <Button
          data-testid={`ingest-reject-${item.id}`}
          onClick={discard}
          variant="ghost"
          className="text-[hsl(var(--ploy-rust))] hover:bg-[hsl(var(--ploy-rust-soft))] font-semibold gap-1.5"
        >
          <XIcon size={14} weight="bold" /> Discard
        </Button>
        <Button
          data-testid={`ingest-confirm-${item.id}`}
          onClick={confirm}
          disabled={busy || !!upsell}
          className="rounded-[10px] font-semibold text-[15px] px-5 py-2.5 bg-[hsl(var(--ploy-ink))] hover:bg-[hsl(var(--ploy-ink))]/90 text-[hsl(var(--ploy-ink-inv))] border-0 gap-1.5"
        >
          <CheckCircle size={16} weight="fill" />
          {busy ? "Filing…" : "File document"}
        </Button>
      </div>
    </div>
  );
}

export default function Ingest() {
  const { fmtAsset } = useDemo();
  const { hid, isOperator, assets, refreshAssets, liabilities, refreshLiabilities, ownerLabels } = useHousehold();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatuses, setUploadStatuses] = useState([]);
  const [customTypes, setCustomTypes] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [pendingDuplicateQueue, setPendingDuplicateQueue] = useState([]);
  const [duplicateBusy, setDuplicateBusy] = useState(false);
  const [queueTotal, setQueueTotal] = useState(null);
  const fileRef = useRef();
  const [packageDialog, setPackageDialog] = useState({ open: false, item: null });

  const loadCustomTypes = useCallback(() => {
    if (!hid) return Promise.resolve();
    return api.get(`/ingest/custom-types?household_id=${hid}`)
      .then((r) => {
        const next = {};
        for (const entry of (r.data?.types || [])) {
          if (entry?.slug && entry?.label) next[entry.slug] = entry.label;
        }
        setCustomTypes(next);
      })
      .catch(() => setCustomTypes({}));
  }, [hid]);

  const addCustomType = useCallback(async (slug, label) => {
    if (!hid) return null;
    const name = label.trim();
    if (!name) return null;
    await api.post("/ingest/custom-types", { slug, label: name }, { params: { household_id: hid } });
    setCustomTypes((current) => ({ ...current, [slug]: name }));
    return slug;
  }, [hid]);

  const load = useCallback(() => {
    if (!hid) return;
    setLoadError(null);
    api.get(`/ingest?household_id=${hid}`)
      .then((r) => {
        setItems(r.data);
        const total = Number(r.headers?.["x-total-count"]);
        setQueueTotal(Number.isFinite(total) ? total : null);
      })
      .catch((e) => {
        setLoadError(e?.message || "Failed to load queue");
        setItems([]);
        setQueueTotal(null);
      });
  }, [hid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCustomTypes(); }, [loadCustomTypes]);

  // Auto-select: keep selectedId valid; default to first item
  useEffect(() => {
    if (!items.length) { setSelectedId(null); return; }
    const ids = new Set(items.map((i) => i.id));
    if (!selectedId || !ids.has(selectedId)) {
      setSelectedId(items[0].id);
    }
  }, [items, selectedId]);

  const selectedItem = items.find((i) => i.id === selectedId) || null;

  const notifyAutoFiledResult = async (af) => {
    const s = af.summary;
    if (s) {
      toast.success(
        `Auto-filed · ${s.operator}`,
        {
          description:
            `$${Number(s.paid_total || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}  ·  ${(s.wells || 0).toLocaleString()} wells  ·  ${(s.lines || 0).toLocaleString()} lines  ·  Check #${s.check_number || "—"} on ${s.check_date || "—"}  ·  ${s.cross_foot_ok ? "Cross-foot OK" : "Cross-foot delta"}  ·  Summary emailed.`,
          duration: 9000,
        },
      );
    } else {
      toast.success(`Auto-filed · ${af.name}`, { description: af.dest, duration: 8000 });
    }
  };

  const buildAutoFiledResult = async (it, fileName, cr) => {
    let summary = null;
    try {
      const nf = await api.post(`/ingest/${it.id}/notify-auto-file?household_id=${hid}`);
      summary = nf.data?.summary;
    } catch (_e) { /* notification best-effort */ }
    return { name: fileName, dest: cr.data?.destination || "Filed", summary };
  };

  const updateUploadStatus = useCallback((index, nextStatus) => {
    setUploadStatuses((current) => current.map((entry, entryIndex) => (
      entryIndex === index ? { ...entry, ...nextStatus } : entry
    )));
  }, []);

  const upload = async (files) => {
    if (!files?.length || !hid || uploading) return;
    const batch = Array.from(files);
    const { rejected } = validateFiles(batch);
    setUploadStatuses(batch.map((file) => ({
      name: file.name,
      status: "queued",
      detail: file.size > MAX_UPLOAD_BYTES ? "Checking file size…" : "Waiting to read…",
    })));
    setUploading(true);
    try {
      const auto_filed = [];
      const manual = [];
      const duplicateQueueAdds = [];
      const results = await runIngestBatch({
        files: batch,
        maxBytes: MAX_UPLOAD_BYTES,
        onStatus: (index, status) => {
          updateUploadStatus(index, {
            name: status.name,
            status: status.status,
            detail: status.detail,
          });
        },
        classifyOne: async (file) => {
          const fd = new FormData();
          fd.append("file", file);

          const r = await api.post(`/ingest/classify?household_id=${hid}`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const it = r.data;

          if (it.auto_file_intent && it.suggested_asset_id && !it.duplicate_of) {
            try {
              const cr = await api.post(`/ingest/${it.id}/confirm?household_id=${hid}`, {
                doc_type: it.doc_type,
                target_asset_id: it.suggested_asset_id,
              });
              const filed = await buildAutoFiledResult(it, file.name, cr);
              auto_filed.push(filed);
              return { detail: `Auto-filed — ${filed.dest}` };
            } catch (e) {
              const det = getErrorDetail(e);
              if (e?.response?.status === 409 && det?.error === "duplicate_document") {
                duplicateQueueAdds.push({
                  detail: det,
                  ingestId: it.id,
                  payload: {
                    doc_type: it.doc_type,
                    target_asset_id: it.suggested_asset_id,
                  },
                  fileName: file.name,
                });
                return { detail: "Duplicate found — confirm the filing in the dialog." };
              }

              manual.push(file.name);
              return { detail: "Read — review and confirm below." };
            }
          }

          manual.push(file.name);
          return { detail: "Read — review and confirm below." };
        },
      });

      if (duplicateQueueAdds.length) {
        setPendingDuplicateQueue((q) => [...q, ...duplicateQueueAdds]);
      }
      for (const af of auto_filed) {
        await notifyAutoFiledResult(af);
      }
      if (manual.length) {
        toast.success(`${manual.length} document${manual.length === 1 ? "" : "s"} ready to review`);
      }
      if (rejected.length) {
        toast.error(`${rejected.length} file${rejected.length === 1 ? "" : "s"} exceeded the ${uploadLimitLabel} limit`);
      }
      const failed = results.filter((result) => result.status === "failed");
      if (failed.length) {
        toast.error(`${failed.length} document${failed.length === 1 ? "" : "s"} could not be read`);
      }
      if (fileRef.current) fileRef.current.value = "";
      load();
      refreshAssets();
    } catch (e) { toast.error(getErrorMessage(e, "Upload failed")); }
    finally { setUploading(false); }
  };

  const addPackageForItem = async (it) => {
    setPackageDialog({ open: true, item: it });
  };

  const activeDuplicate = pendingDuplicateQueue[0] || null;

  const viewDuplicate = async (duplicateOf) => {
    if (!duplicateOf?.archive_source || !duplicateOf?.archive_item_id) return;
    try {
      const popup = window.open("", "_blank");
      const r = await api.get(
        `/archive/items/${duplicateOf.archive_source}/${duplicateOf.archive_item_id}/file`,
        { params: { household_id: hid }, responseType: "blob" }
      );
      const url = URL.createObjectURL(
        r.data instanceof Blob ? r.data : new Blob([r.data], { type: "application/pdf" })
      );
      if (popup) popup.location = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (_e) {
      toast.error("Could not open document");
    }
  };

  const confirmQueuedDuplicate = async () => {
    if (!activeDuplicate) return;
    setDuplicateBusy(true);
    try {
      const cr = await api.post(`/ingest/${activeDuplicate.ingestId}/confirm?household_id=${hid}`, {
        ...activeDuplicate.payload,
        confirm_duplicate: true,
      });
      const af = await buildAutoFiledResult({ id: activeDuplicate.ingestId }, activeDuplicate.fileName, cr);
      await notifyAutoFiledResult(af);
      setPendingDuplicateQueue((q) => q.slice(1));
      onDone();
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not file"));
    } finally {
      setDuplicateBusy(false);
    }
  };

  const onDone = () => { load(); refreshAssets(); refreshLiabilities(); };

  const openFilePicker = () => {
    if (uploading) return;
    fileRef.current?.click();
  };
  const dropzoneKeyDown = (e) => { if ((e.key === "Enter" || e.key === " ") && !uploading) { e.preventDefault(); openFilePicker(); } };

  return (
    <div
      className="space-y-8 animate-fade-up min-h-screen -m-6 p-6 lg:-m-10 lg:p-10 bg-[hsl(var(--ploy-bg))]"
      data-testid="ingest-page"
    >
      {/* ── Page header ── */}
      <div className="flex items-start gap-4 pb-6 border-b border-[hsl(var(--ploy-border))]">
        <div className="h-12 w-12 rounded-xl bg-[hsl(var(--ploy-ink))] text-[hsl(var(--ploy-ink-inv))] flex items-center justify-center shadow-sm shrink-0">
          <Tray size={22} weight="regular" />
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[hsl(var(--ploy-brass-deep))]">
            Operations · Document Inbox
          </p>
          <h1 className="font-serif font-medium tracking-tight text-[44px] sm:text-[52px] leading-[1.05] mt-1 text-[hsl(var(--ploy-ink))]" data-testid="page-title">
            Inbox
          </h1>
          <p className="text-sm text-[hsl(var(--ploy-ink-soft))] mt-2 max-w-xl leading-relaxed">
            Drop any document — a lease, insurance declaration, property-tax or mortgage statement,
            a mineral/royalty check stub, or anything else. We read it, suggest where it belongs,
            and file it on your confirmation.
          </p>
        </div>
      </div>

      {/* ── Two-column row: Upload + Queue ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[38%_1fr] gap-6 items-start">
        {/* Left — Upload panel */}
        {isOperator && (
          <div className="bg-[hsl(var(--ploy-card))] border border-[hsl(var(--ploy-border))] rounded-2xl p-5">
            <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[hsl(var(--ploy-brass-deep))] mb-3">Upload</p>
            <div
              data-testid="ingest-dropzone"
              role="button"
              tabIndex={0}
              aria-label="Upload documents — PDF or image, multiple at once"
              onClick={openFilePicker}
              onKeyDown={dropzoneKeyDown}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); if (!uploading) upload(Array.from(e.dataTransfer.files)); }}
              className="border-2 border-dashed border-[hsl(var(--ploy-border))] rounded-xl p-6 text-center cursor-pointer
                hover:border-[hsl(var(--ploy-brass))] hover:bg-[hsl(var(--ploy-panel))]/60 transition-colors
                bg-[hsl(var(--ploy-bg-sec))]/50
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))] focus-visible:ring-offset-2"
            >
              <input ref={fileRef} type="file" accept="application/pdf,image/*" multiple hidden onChange={(e) => upload(Array.from(e.target.files))} data-testid="ingest-file-input" />
              <div className="h-10 w-10 mx-auto rounded-lg bg-[hsl(var(--ploy-panel))] text-[hsl(var(--ploy-brass-deep))] flex items-center justify-center mb-3">
                <Tray size={18} weight="regular" />
              </div>
              <p className="font-serif text-lg tracking-tight text-[hsl(var(--ploy-ink))]">
                {uploading ? "Reading documents…" : "Drop or click to upload"}
              </p>
              <p className="text-[12px] text-[hsl(var(--ploy-ink-soft))] mt-1">PDF or image · multiple at once</p>
              <Button
                data-testid="ingest-choose-files"
                disabled={uploading}
                className="mt-3 rounded-[10px] font-semibold text-[13px] px-4 py-2 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 border-0"
                onClick={(e) => { e.stopPropagation(); openFilePicker(); }}
              >
                Choose files
              </Button>
            </div>
            <p className="text-[11px] text-[hsl(var(--ploy-ink-soft))] mt-2.5 leading-relaxed">
              Lease, mortgage statement, property-tax bill, insurance declaration, PM statement, mineral check stub… Max {uploadLimitLabel} each.
            </p>
            {uploadStatuses.length > 0 && (
              <div
                className="mt-4 rounded-xl border border-[hsl(var(--ploy-border))] bg-[hsl(var(--ploy-panel))]/75 overflow-hidden"
                data-testid="ingest-upload-status-list"
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-[hsl(var(--ploy-border))]">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[hsl(var(--ploy-brass-deep))]">
                      Current batch
                    </p>
                    <p className="text-[12px] text-[hsl(var(--ploy-ink-soft))] mt-1">
                      Each document finishes independently, even if another one fails.
                    </p>
                  </div>
                  <span className="text-[11px] font-bold tabular-nums rounded-full px-2.5 py-0.5 bg-[hsl(var(--ploy-bg-sec))] text-[hsl(var(--ploy-ink-soft))]">
                    {uploadStatuses.length} {uploadStatuses.length === 1 ? "file" : "files"}
                  </span>
                </div>
                <div className="divide-y divide-[hsl(var(--ploy-border))]">
                  {uploadStatuses.map((entry, index) => {
                    const statusTone = entry.status === "done"
                      ? "bg-[hsl(var(--ploy-pos))]/10 text-[hsl(var(--ploy-pos))] border-[hsl(var(--ploy-pos))]/20"
                      : entry.status === "failed"
                        ? "bg-[hsl(var(--ploy-rust))]/10 text-[hsl(var(--ploy-rust))] border-[hsl(var(--ploy-rust))]/20"
                        : entry.status === "rejected"
                          ? "bg-[hsl(35_70%_94%)] text-[hsl(var(--ploy-brass-deep))] border-[hsl(35_70%_75%)]"
                          : "bg-[hsl(var(--ploy-bg-sec))] text-[hsl(var(--ploy-ink-soft))] border-[hsl(var(--ploy-border))]";
                    const statusLabel = entry.status === "done"
                      ? "Done"
                      : entry.status === "failed"
                        ? "Failed"
                        : entry.status === "rejected"
                          ? "Rejected"
                          : entry.status === "reading"
                            ? "Reading"
                            : "Queued";

                    return (
                      <div key={`${entry.name}-${index}`} className="px-4 py-3 flex items-start gap-3">
                        <div className="mt-0.5 h-9 w-9 rounded-lg bg-[hsl(var(--ploy-card))] border border-[hsl(var(--ploy-border))] flex items-center justify-center text-[hsl(var(--ploy-brass-deep))] shrink-0">
                          <FilePdf size={16} weight="regular" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-[hsl(var(--ploy-ink))] truncate">{entry.name}</p>
                              <p className="text-[12px] text-[hsl(var(--ploy-ink-soft))] mt-1 leading-relaxed">
                                {entry.detail || (entry.status === "reading" ? "Reading document…" : "Waiting to read…")}
                              </p>
                            </div>
                            <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] font-bold ${statusTone}`}>
                              {statusLabel}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Right — Awaiting confirmation queue */}
        <div className="bg-[hsl(var(--ploy-card))] border border-[hsl(var(--ploy-border))] rounded-2xl overflow-hidden" data-testid="ingest-queue">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[hsl(var(--ploy-border))]">
            <h2 className="font-serif text-xl tracking-tight text-[hsl(var(--ploy-ink))]">Awaiting confirmation</h2>
            <div className="flex flex-col items-end gap-1">
              <span data-testid="ingest-count" className="text-[11px] font-bold tabular-nums rounded-full px-2.5 py-0.5 bg-[hsl(var(--ploy-bg-sec))] text-[hsl(var(--ploy-ink-soft))]">
                {items.length} {items.length === 1 ? "document" : "documents"}
              </span>
              {queueTotal != null && queueTotal > items.length && (
                <p
                  data-testid="ingest-queue-cap-note"
                  className="text-[11px] text-[hsl(var(--ploy-ink-soft))] text-right"
                >
                  Showing {items.length} of {queueTotal} queued documents.
                </p>
              )}
            </div>
          </div>

          {loadError ? (
            <div className="p-8 text-center" data-testid="ingest-error">
              <p className="text-sm font-semibold text-[hsl(var(--ploy-rust))]">Couldn&apos;t load your queue</p>
              <p className="text-[12px] text-[hsl(var(--ploy-ink-soft))] mt-1">Check your connection and try again.</p>
              <Button variant="outline" onClick={load} className="mt-3 text-[12px] rounded-lg" data-testid="ingest-retry">Retry</Button>
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center" data-testid="ingest-empty">
              <CheckCircle size={24} weight="fill" className="mx-auto text-[hsl(var(--ploy-pos,var(--success)))] mb-2 opacity-80" />
              <p className="font-serif text-lg text-[hsl(var(--ploy-ink))]">Nothing to review</p>
              <p className="text-[12px] text-[hsl(var(--ploy-ink-soft))] mt-1">Filed documents land on their matching property, mortgage, or asset.</p>
            </div>
          ) : (
            <>
              {/* Column headers */}
              <div className="grid grid-cols-[1fr_auto_auto_24px] gap-3 px-5 py-2 text-[10px] uppercase tracking-[0.12em] font-bold text-[hsl(var(--ploy-brass-deep))] bg-[hsl(var(--ploy-bg-sec))] border-b border-[hsl(var(--ploy-border))]">
                <span>Document</span><span>Type</span><span className="hidden sm:block">Suggested file</span><span />
              </div>
              {/* Rows */}
              <div className="divide-y divide-[hsl(var(--ploy-border))]" data-testid="ingest-rows">
                {items.map((it) => (
                  <IngestRow
                    key={it.id}
                    item={it}
                    assets={assets}
                    liabilities={liabilities}
                    selected={it.id === selectedId}
                    onSelect={() => setSelectedId(it.id)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Detail panel (only when a row is selected) ── */}
      {selectedItem && (
        <div
          className="border-t-[3px] border-[hsl(var(--accent))] rounded-2xl shadow-md bg-[hsl(var(--ploy-card))] border border-[hsl(var(--ploy-border))] p-5 sm:p-6"
          data-testid="ingest-detail-panel"
        >
          <IngestCard
            key={selectedItem.id}
            item={selectedItem}
            assets={assets}
            liabilities={liabilities}
            hid={hid}
            onDone={onDone}
            customTypes={customTypes}
            addCustomType={addCustomType}
            addPackage={() => addPackageForItem(selectedItem)}
            refreshAssets={refreshAssets}
            refreshLiabilities={refreshLiabilities}
            viewDuplicate={viewDuplicate}
          />
        </div>
      )}

      <ConfirmDuplicateDialog
        open={!!activeDuplicate}
        onOpenChange={(open) => {
          if (!open && !duplicateBusy) setPendingDuplicateQueue((q) => q.slice(1));
        }}
        detail={activeDuplicate?.detail}
        busy={duplicateBusy}
        onConfirm={confirmQueuedDuplicate}
        onView={viewDuplicate}
      />

      <NamedInputDialog
        open={packageDialog.open}
        onOpenChange={(open) => {
          if (!open) setPackageDialog({ open: false, item: null });
        }}
        title="Add mineral package"
        description="Name the mineral package before filing this document."
        label="Package name"
        placeholder="New mineral package"
        initialValue={packageDialog.item?.suggested_package_name || "New mineral package"}
        confirmLabel="Add package"
        busyLabel="Adding…"
        testId="ingest-package-dialog"
        validate={(value) => (value ? "" : "Enter a package name.")}
        onSubmit={async (name) => {
          const item = packageDialog.item;
          try {
            const r = await api.post(`/assets?household_id=${hid}`, {
              name,
              asset_type: "mineral_royalty",
              owner_party: "joint",
              current_value: 0,
              confidence: "low",
              review_status: "needs_review",
              meta: {
                owner_code: item?.suggested_owner_code || "",
                operator: item?.suggested_package_name || "",
              },
            });
            toast.success(`Added "${r.data.name}" — now select it in the picker`);
            refreshAssets();
            setPackageDialog({ open: false, item: null });
          } catch (e) {
            toast.error(getErrorMessage(e, "Could not add package"));
            throw e;
          }
        }}
      />

      <KnownSendersManager householdId={hid} />
    </div>
  );
}

/* ── IngestRow — compact list row for the queue ── */
const DOC_TYPE_LABELS_ROW = {
  lease: "Lease", insurance_declaration: "Insurance", property_tax: "Property Tax",
  pm_statement: "PM Statement", mortgage_statement: "Mortgage",
  mineral_statement: "Mineral", other: "Other",
};

function IngestRow({ item, assets, liabilities, selected, onSelect }) {
  const fname = item.file?.filename || "Untitled";
  const dt = item.doc_type || "other";
  const typeLabel = DOC_TYPE_LABELS_ROW[dt] || dt;
  const ago = item.created_at ? _timeAgo(item.created_at) : "";

  // Suggested destination
  let dest = "No suggestion";
  if (item.suggested_liability_id) {
    const l = liabilities.find((x) => x.id === item.suggested_liability_id);
    if (l) dest = l.name;
  } else if (item.suggested_asset_id) {
    const a = assets.find((x) => x.id === item.suggested_asset_id);
    if (a) dest = a.name;
  }

  return (
    <div
      data-testid={`ingest-row-${item.id}`}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
      className={`grid grid-cols-[1fr_auto_auto_24px] gap-3 items-center px-5 py-3 cursor-pointer transition-colors
        border-l-[3px]
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[hsl(var(--accent))]
        ${selected
          ? "border-l-[hsl(var(--accent))] bg-[hsl(var(--ploy-panel))]"
          : "border-l-transparent hover:bg-[hsl(var(--ploy-row-hover))]"
        }`}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <FilePdf size={18} weight="duotone" className="text-[hsl(var(--ploy-ink-3))] shrink-0" />
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-[hsl(var(--ploy-ink))] truncate">{fname}</p>
          {ago && <p className="text-[11px] text-[hsl(var(--ploy-ink-soft))]">scanned {ago}</p>}
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] font-semibold rounded-md px-2 py-0.5 bg-[hsl(var(--ploy-bg-sec))] text-[hsl(var(--ploy-ink-soft))]">
          {typeLabel}
        </span>
        {item.ai_used && (
          <span className="text-[10px] font-bold rounded-md px-1.5 py-0.5 bg-[hsl(var(--ploy-panel))] text-[hsl(var(--ploy-brass-deep))] flex items-center gap-0.5">
            <Sparkle size={9} weight="fill" /> AI
          </span>
        )}
      </div>
      <span className="text-[12px] text-[hsl(var(--ploy-ink-soft))] truncate max-w-[180px] hidden sm:block">{dest}</span>
      <ArrowRight size={14} className={`shrink-0 transition-colors ${selected ? "text-[hsl(var(--accent))]" : "text-[hsl(var(--ploy-ink-4))]"}`} />
    </div>
  );
}

function _timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
